import type { ChatRoomSummary } from '@tomato/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../api/client';
import { EmptyState, Notice, PostListSkeleton } from '../components/ui';
import { formatWhen } from '../lib/markdown';
import { useRefreshStore } from '../store/refresh';

function RoomRow({ room }: { room: ChatRoomSummary }) {
  return (
    <Link
      viewTransition
      to={`/chats/${room.id}`}
      className={`flex items-center gap-3 px-1 py-4 transition active:opacity-60 sm:hover:opacity-70 ${
        room.closed ? 'opacity-50' : ''
      }`}
    >
      {room.post.thumbnailUrl ? (
        <img
          src={room.post.thumbnailUrl}
          alt=""
          loading="lazy"
          className="size-12 shrink-0 rounded-xl bg-ink-100 object-cover"
        />
      ) : (
        <div className="grid size-12 shrink-0 place-items-center rounded-xl bg-ink-100 text-ink-300">
          🍅
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[14px] font-medium text-ink-900">{room.partnerNickname}</span>
          {room.lastMessageAt && (
            <span className="text-[12px] text-ink-400">{formatWhen(room.lastMessageAt)}</span>
          )}
          {room.unread && <span className="ml-auto size-2 shrink-0 rounded-full bg-tomato-600" />}
        </div>
        <p className="mt-0.5 truncate text-[13px] text-ink-400">{room.post.title}</p>
        {room.lastMessagePreview && (
          <p className="mt-0.5 truncate text-[13px] text-ink-500">{room.lastMessagePreview}</p>
        )}
      </div>
    </Link>
  );
}

export function ChatListScreen() {
  const refreshTick = useRefreshStore((s) => s.tick);
  const [items, setItems] = useState<ChatRoomSummary[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    void api
      .get<{ items: ChatRoomSummary[] }>('/api/me/chats')
      .then((res) => !cancelled && setItems(res.items))
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : '대화를 불러오지 못했어요.');
        setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshTick]); // 당겨서 새로고침이 tick을 올리면 다시 불러온다

  return (
    <div className="py-6">
      <h1 className="mb-5 text-[26px] font-semibold tracking-tight text-ink-900">대화</h1>

      {items === null ? (
        <PostListSkeleton rows={4} />
      ) : items.length === 0 ? (
        <EmptyState
          icon="💬"
          title="아직 대화가 없어요"
          body="장터에서 찜한 물건의 판매자와 대화를 시작해보세요."
        />
      ) : (
        <div className="overflow-hidden rounded-2xl bg-surface px-4 shadow-card">
          {items.map((room, i) => (
            <div key={room.id}>
              {i > 0 && <div className="h-px bg-ink-100" />}
              <RoomRow room={room} />
            </div>
          ))}
        </div>
      )}

      <Notice>{error}</Notice>
    </div>
  );
}
